/**
 * פונקציות ייעודיות לטיפול בטקסט עברי
 * Hebrew Text Processing Utilities
 */

// ========================================
// קבועים / Constants
// ========================================

export const HEBREW_LETTERS = [
  'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י',
  'יא', 'יב', 'יג', 'יד', 'טו', 'טז', 'יז', 'יח', 'יט', 'כ',
  'כא', 'כב', 'כג', 'כד', 'כה', 'כו', 'כז', 'כח', 'כט', 'ל',
  'לא', 'לב', 'לג', 'לד', 'לה', 'לו', 'לז', 'לח', 'לט', 'מ',
  'מא', 'מב', 'מג', 'מד', 'מה', 'מו', 'מז', 'מח', 'מט', 'נ',
  'נא', 'נב', 'נג', 'נד', 'נה', 'נו', 'נז', 'נח', 'נט', 'ס',
  'סא', 'סב', 'סג', 'סד', 'סה', 'סו', 'סז', 'סח', 'סט', 'ע',
  'עא', 'עב', 'עג', 'עד', 'עה', 'עו', 'עז', 'עח', 'עט', 'פ',
  'פא', 'פב', 'פג', 'פד', 'פה', 'פו', 'פז', 'פח', 'פט', 'צ',
  'צא', 'צב', 'צג', 'צד', 'צה', 'צו', 'צז', 'צח', 'צט', 'ק',
  'קא', 'קב', 'קג', 'קד', 'קה', 'קו', 'קז', 'קח', 'קט', 'ר',
];

// ========================================
// טיפוסים / Types
// ========================================

type GematriaValue = [number, string];
type SpecialCases = { [key: string]: string };

// ========================================
// פונקציות עזר בסיסיות / Basic Helper Functions
// ========================================

/**
 * המרת אינדקס למספר עברי
 * Convert index to Hebrew letter
 */
export const formatToHebrewLetter = (index: number): string => 
  HEBREW_LETTERS[index];

/**
 * תקנון איות עברי - מסיר או מחליף תווים מסוימים
 * Standardize Hebrew spelling by removing or replacing certain characters
 */
export const standardizeHebrewSpelling = (word = ''): string => {
  const replacements: { [key: string]: string } = {
    'ן': 'ם',
    'ו': '',
    'י': ''
  };

  return word
    .split('')
    .map((char, index) => {
      // שמור ו' ו-י' בתחילת או סוף המילה
      if ((char === 'ו' || char === 'י') && (index === 0 || index === word.length - 1)) {
        return char;
      }
      // החלף או הסר תווים לפי המפתח
      return replacements[char] !== undefined ? replacements[char] : char;
    })
    .join('');
};

// ========================================
// פונקציות גימטריה / Gematria Functions
// ========================================

/**
 * המרת מספר לגימטריה עברית
 * Convert number to Hebrew gematria
 * @param num - המספר להמרה
 * @param withGeresh - האם להוסיף גרש או גרשיים
 */
export const numberToGematria = (num: number | string, withGeresh = false): string => {
  if (typeof num === 'string') return num;

  const gematriaValues: GematriaValue[] = [
    [400, 'ת'], [300, 'ש'], [200, 'ר'], [100, 'ק'], [90, 'צ'], [80, 'פ'], 
    [70, 'ע'], [60, 'ס'], [50, 'נ'], [40, 'מ'], [30, 'ל'], [20, 'כ'], 
    [10, 'י'], [9, 'ט'], [8, 'ח'], [7, 'ז'], [6, 'ו'], [5, 'ה'], 
    [4, 'ד'], [3, 'ג'], [2, 'ב'], [1, 'א']
  ];

  // מקרים מיוחדים שנמנעים מסיבות דתיות
  const specialCases: SpecialCases = {
    'רע': 'ער',   // רע -> ער
    'יה': 'טו',   // יה -> טו
    'יו': 'טז',   // יו -> טז
    'מת': 'תמ'    // מת -> תמ
  };

  let result = '';
  let remainingNum = num;

  // בנה את התוצאה לפי ערכי הגימטריה
  for (const [value, letter] of gematriaValues) {
    while (remainingNum >= value) {
      result += letter;
      remainingNum -= value;
    }
  }

  // החלף מקרים מיוחדים
  result = specialCases[result] || result;

  // הוסף גרש או גרשיים אם נדרש
  if (!withGeresh) {
    return result;
  }

  if (result.length === 1) {
    return result + '׳';  // גרש לאות יחידה
  } else {
    return result.slice(0, -1) + '״' + result.slice(-1);  // גרשיים לכמה אותיות
  }
};

/**
 * המרת אותיות עבריות למספר (גימטריה)
 * Convert Hebrew letters to number (gematria)
 */
export const hebrewLettersToNumber = (hebrew: string): number => {
  const gematriaValues: { [key: string]: number } = {
    'א': 1, 'ב': 2, 'ג': 3, 'ד': 4, 'ה': 5, 'ו': 6, 'ז': 7, 'ח': 8, 'ט': 9,
    'י': 10, 'כ': 20, 'ל': 30, 'מ': 40, 'נ': 50, 'ס': 60, 'ע': 70, 'פ': 80, 'צ': 90,
    'ק': 100, 'ר': 200, 'ש': 300, 'ת': 400
  };

  let total = 0;
  for (const char of hebrew) {
    if (gematriaValues[char]) {
      total += gematriaValues[char];
    }
  }
  
  return total;
};
